// KPI sabitleri ve etiketler — schemas/kpi.ts 600 satır bölmesi (TODO-KPI-114/-144, salt taşıma); `kpi.ts` barrel'i yeniden dışa açar.
import { z } from "zod";

// ---- Sabitler ----
export const KPI_VALUE_TYPES = ["number", "percent", "text"] as const;
export const KPI_AGGREGATIONS = ["sum", "avg", "weighted_avg", "none", "recompute"] as const;
/** Konsolide/ara toplam kuralı etiketleri — katalog ve set penceresi aynı sözlüğü kullanır. */
export const KPI_AGGREGATION_LABELS: Record<(typeof KPI_AGGREGATIONS)[number], string> = {
  sum: "Toplanır",
  avg: "Ortalama",
  weighted_avg: "Ağırlıklı ortalama",
  none: "Toplanmaz (oran)",
  recompute: "Yeniden hesaplanır",
};
/**
 * Konsolide/ara toplam kuralı açıklamaları — katalog + set penceresinde ipucu, kılavuzda tablo
 * (2026-10-08 manuel test: seçenekler tek tek açıklanmalı). `example` "Üretim 1.200.000 +
 * Lojistik 300.000 kWh" senaryo sayılarıyla; `forDerived` yalnız formüllü göstergede anlamlı.
 * TODO-KPI-32 (0053): weighted_avg ağırlık göstergesi taşır — Σ(değer×ağırlık) / Σ ağırlık.
 */
export interface KpiAggregationHelp {
  summary: string;
  example: string;
  bestFor: string;
  forDerivedOnly?: boolean;
  caveat?: string;
}
export const KPI_AGGREGATION_HELP: Record<(typeof KPI_AGGREGATIONS)[number], KpiAggregationHelp> = {
  sum: {
    summary: "Dolu işletme değerleri toplanır; bir işletme boş ya da XXX ise toplam kısmi sayılır.",
    example: "1.200.000 + 300.000 = 1.500.000 kWh",
    bestFor: "miktar, tutar, adet (ham gösterge)",
  },
  avg: {
    summary: "Dolu işletme değerlerinin aritmetik ortalaması.",
    example: "(1.200.000 + 300.000) / 2 = 750.000",
    bestFor: "skor, puan, sıcaklık gibi toplanması anlamsız değerler",
  },
  weighted_avg: {
    summary: "Her işletmenin değeri bir ağırlıkla (üretim, çalışan…) tartılıp ortalanır.",
    example: "(2×100 + 4×300) / 400 = 3,5 (ağırlık: üretim miktarı)",
    bestFor: "işletme büyüklüğüne göre tartılması gereken oranlar",
  },
  none: {
    summary: 'Konsolide ve ara toplam hücresi boş ("-") kalır; oranlar toplanamaz.',
    example: 'kişi başı 5.000 + 5.000 = 10.000 olmaz → "-"',
    bestFor: "elle girilen oran, yüzde, yoğunluk",
  },
  recompute: {
    summary: "Formül, işletme sonuçları yerine konsolide girdilerle yeniden hesaplanır.",
    example: "1.500.000 / 300 = 5.000 kWh/kişi",
    bestFor: "formülle hesaplanan oranlar (a / b) — hesaplanan göstergede varsayılan",
    forDerivedOnly: true,
  },
};
export const KPI_METRIC_STATUSES = ["active", "deprecated"] as const;
export const KPI_REPORT_SCOPES = ["gri", "entegre", "diger"] as const;
export const KPI_REPORT_TABLE_LAYOUTS = ["entities", "consolidated", "both"] as const;
export const KPI_CLIENT_ACCESS = ["inherit", "closed", "readonly", "open"] as const;
export const KPI_VALUE_STATUSES = ["empty", "pending_xxx", "filled", "not_measured"] as const;
export const KPI_WORKFLOW_STATES = [
  "draft",
  "submitted",
  "clarification",
  "verified",
  "client_approved",
  "locked",
] as const;
export const KPI_UPDATED_BY_KINDS = ["internal", "client"] as const;
export const KPI_EVIDENCE_RELATION_TYPES = ["import_source", "attached"] as const;
export const KPI_IMPORT_JOB_STATUSES = [
  "draft",
  "queued",
  "extracting",
  "mapping",
  "ready",
  "committed",
  "failed",
] as const;
// "reported" = K20 (eşleştirme satırı eylemi); hafıza tablosu da "reported" saklar (B-05).
export const KPI_MAPPING_ACTIONS = [
  "map",
  "skip",
  "create",
  "reported",
  "create_breakdown",
] as const;

// --- Kırılım hiyerarşisi (spec §17/§18) ---
export const KPI_NODE_KINDS = ["category", "metric", "breakdown_group"] as const;
export type KpiNodeKind = (typeof KPI_NODE_KINDS)[number];
export const KPI_BREAKDOWN_ROLLUPS = ["sum", "avg", "none"] as const;
export const KPI_BREAKDOWN_DIMENSIONS = [
  "cinsiyet",
  "yaka",
  "yas_grubu",
  "calisma_turu",
  "sozlesme_turu",
  "bolge",
  "kademe",
  "egitim",
  "atik_turu",
  "enerji_kaynagi",
  "kapsam",
  "diger",
] as const;
export type KpiBreakdownDimension = (typeof KPI_BREAKDOWN_DIMENSIONS)[number];
export const KPI_BREAKDOWN_DIMENSION_LABELS: Record<
  (typeof KPI_BREAKDOWN_DIMENSIONS)[number],
  string
> = {
  cinsiyet: "Cinsiyete göre",
  yaka: "Yakaya göre",
  yas_grubu: "Yaş grubuna göre",
  calisma_turu: "Çalışma türüne göre",
  sozlesme_turu: "Sözleşme türüne göre",
  bolge: "Bölgeye göre",
  kademe: "Kademeye göre",
  egitim: "Eğitime göre",
  atik_turu: "Atık türüne göre",
  enerji_kaynagi: "Enerji kaynağına göre",
  kapsam: "Kapsama göre",
  diger: "Diğer",
};
export const KpiBreakdownSchema = z.object({
  dimension: z.string().min(1).max(40),
  complete: z.boolean(),
  rollup: z.enum(KPI_BREAKDOWN_ROLLUPS),
});
export type KpiBreakdown = z.infer<typeof KpiBreakdownSchema>;

/** Hücre `failure` değerleri (şema `z.string()` kalır — tel uyumu). */
export const KPI_CELL_FAILURES = [
  "wavg_count_mismatch",
  "zero_denominator",
  "empty_group",
  "breakdown_group_missing",
  "breakdown_member_missing",
  /** Formül döngüsündeki (ya da döngüye bağımlı) gösterge — yalnız o kodlar HESAPLANAMADI. */
  "cycle",
] as const;

/** Spec §18 hata kodu → metin tablosu (DR5) kodları. */
export const KPI_BREAKDOWN_ERROR_CODES = [
  "duplicate_dimension",
  "duplicate_member",
  "breakdown_member_client_mismatch",
  "member_requires_group",
  "group_under_category",
  "breakdown_on_non_group",
  "breakdown_depth",
  "breakdown_member_move",
  // §3.3 set ağacı kuralı: grup/üye sete yalnız katalog üstünün set satırının altına eklenir
  "breakdown_member_parent",
  "breakdown_group_conflict",
  "calculation_group_incomplete",
  "breakdown_group_missing",
  "breakdown_member_missing",
  "breakdown_mismatch",
  "legacy_metric_children",
  "KPI_GROUP_READONLY",
  "KPI_DERIVED_READONLY",
  "KPI_NODE_KIND_IN_USE",
  "breakdown_convert_category_has_values",
  "breakdown_unit_mismatch",
  "unit_change_on_breakdown_child",
] as const;
export const KPI_TARGET_TYPES = ["absolute", "relative", "intensity"] as const;
export const KPI_TARGET_STATUSES = ["active", "revised", "achieved", "missed"] as const;
export const CLIENT_ENTITY_TYPES = ["group", "legal_entity", "business_unit", "facility"] as const;
export const EVIDENCE_SOURCE_TYPES = ["upload", "import", "manual"] as const;
/**
 * TODO-EVIDENCE-02 — kanıt belgesi gizlilik sınıfı. `internal` dosya portalda müşteriye HİÇ
 * inmez (C2'ye ek kural, `isClientVisible`); `client` müşteriye erişim politikasıyla iner;
 * `public` ileride dış paylaşım için ayrılmış (v1'de `client` ile aynı davranır).
 */
export const EVIDENCE_CONFIDENTIALITY = ["internal", "client", "public"] as const;
export const EVIDENCE_CONFIDENTIALITY_LABELS: Record<
  (typeof EVIDENCE_CONFIDENTIALITY)[number],
  string
> = {
  internal: "Yalnız danışman (iç)",
  client: "Müşteriye açık",
  public: "Herkese açık",
};
/** Retention politikası değerleri: sistem geneli gün sayısı / asla silme / açık gün (`<n>d`). */
export const EVIDENCE_RETENTION_POLICY_SYSTEM = "system";
export const EVIDENCE_RETENTION_POLICY_KEEP = "keep";
/** Dosya bazlı politika sözdizimi: `system` | `keep` | `<n>d` (1–5 hane). */
export const EVIDENCE_RETENTION_POLICY_RE = /^(system|keep|\d{1,5}d)$/u;
/**
 * Tarama durumu (DB enum `attachment_scan_status_type`, kanıt + form eki ortak). TODO-EVIDENCE-05:
 * `error` = tarama tamamlanamadı (clamd erişilemedi / nesne yok) — dosya erişime kapalı kalır,
 * 10 dk'lık cron yeniden dener (eski model: pending + scanError).
 */
export const EVIDENCE_SCAN_STATUSES = [
  "pending",
  "clean",
  "infected",
  "quarantined",
  "error",
] as const;

export type KpiValueType = (typeof KPI_VALUE_TYPES)[number];
export type KpiAggregation = (typeof KPI_AGGREGATIONS)[number];
export type KpiMetricStatus = (typeof KPI_METRIC_STATUSES)[number];
export type KpiReportScope = (typeof KPI_REPORT_SCOPES)[number];
export type KpiReportTableLayout = (typeof KPI_REPORT_TABLE_LAYOUTS)[number];
export type KpiClientAccess = (typeof KPI_CLIENT_ACCESS)[number];
export type KpiValueStatus = (typeof KPI_VALUE_STATUSES)[number];
export type KpiWorkflowState = (typeof KPI_WORKFLOW_STATES)[number];
export type KpiUpdatedByKind = (typeof KPI_UPDATED_BY_KINDS)[number];
export type KpiEvidenceRelationType = (typeof KPI_EVIDENCE_RELATION_TYPES)[number];
export type KpiImportJobStatus = (typeof KPI_IMPORT_JOB_STATUSES)[number];
export type KpiMappingAction = (typeof KPI_MAPPING_ACTIONS)[number];
export type KpiTargetType = (typeof KPI_TARGET_TYPES)[number];
export type KpiTargetStatus = (typeof KPI_TARGET_STATUSES)[number];
export type ClientEntityType = (typeof CLIENT_ENTITY_TYPES)[number];
export type EvidenceSourceType = (typeof EVIDENCE_SOURCE_TYPES)[number];
export type EvidenceConfidentiality = (typeof EVIDENCE_CONFIDENTIALITY)[number];
export type EvidenceScanStatus = (typeof EVIDENCE_SCAN_STATUSES)[number];

/** Çözümlenmiş müşteri erişimi — `inherit` zinciri çözüldükten sonra kalan üç değer. */
export const KPI_EFFECTIVE_CLIENT_ACCESS = ["closed", "readonly", "open"] as const;
export type KpiEffectiveClientAccess = (typeof KPI_EFFECTIVE_CLIENT_ACCESS)[number];

// ---- Türkçe etiketler (UI) ----
// Finar rev. madde 17 (2026-10-30): müşteri girdisi onay sırası → "Danışman onayında"; değeri henüz
// olmayan yer tutucu → "Değer bekleniyor". Hücre/Word/Excel/prompt metni "XXX" DEĞİŞMEZ (K11).
export const KPI_VALUE_STATUS_LABELS: Record<KpiValueStatus, string> = {
  empty: "Boş",
  pending_xxx: "Değer bekleniyor",
  filled: "Dolu",
  not_measured: "Ölçülmedi",
};
export const KPI_WORKFLOW_STATE_LABELS: Record<KpiWorkflowState, string> = {
  draft: "Taslak",
  submitted: "Danışman onayında",
  clarification: "Açıklama istendi",
  verified: "Onaylandı",
  // TODO-KPI-10: şirket yöneticisi şirket adına onayladı; danışman onayı HÂLÂ gerekir (rapor/AI'da
  // `verified` sayılmaz — resolve-core `isUnverified`).
  client_approved: "Şirket onayladı · danışman onayında",
  locked: "Kilitli",
};
export const KPI_CLIENT_ACCESS_LABELS: Record<KpiClientAccess, string> = {
  inherit: "Üstten devralır",
  closed: "Kapalı",
  readonly: "Yalnız görüntüleme",
  open: "Müşteri girebilir",
};
/**
 * M17 — set erişim rolü (`kpi_set_access.role`, 0056). `owner` veri girer/kanıt yükler;
 * `auditor` seti yalnız görüntüler (kanıt indirme mevcut kurala tabidir). `reviewer`/`approver`
 * davranışı tanımlanmadan şemaya girmez (TODO-KPI-40).
 */
export const KPI_ACCESS_ROLES = ["owner", "auditor"] as const;
export type KpiAccessRole = (typeof KPI_ACCESS_ROLES)[number];
export const KPI_ACCESS_ROLE_LABELS: Record<KpiAccessRole, string> = {
  owner: "Veri sahibi",
  auditor: "Denetçi (yalnız görüntüler)",
};

// ---- Portal yetki modeli v2 (Dalga 3 — spec 2026-10-23 §2/§3.1/§4) ----
/** Kategori izni seviyesi (`kpi_set_access.category_access` değeri); girdisi olmayan düğüm gizlidir. */
export type KpiCategoryLevel = "open" | "readonly";
/** metricId → seviye; en yakın ata-ya-da-kendisi girdisi kazanır. NULL harita = tam erişim. */
export type KpiCategoryAccessMap = Record<string, KpiCategoryLevel>;
/** Kullanıcının tek göstergedeki geçersiz kılması — kategori seviyesinin yerine geçer (Y4). */
export interface KpiIndicatorOverride {
  setIndicatorId: string;
  access: KpiEffectiveClientAccess;
}
/** Üç durumlu seçici etiketleri (müşteri yöneticisi çekmecesi; tek kaynak). */
export const KPI_CATEGORY_ACCESS_LABELS: Record<KpiEffectiveClientAccess, string> = {
  closed: "Gizli",
  readonly: "Görüntüle",
  open: "Düzenle",
};
/** Toplam sütunu türü (Y6): tam kapsam → konsolide; kısmi kapsam (≥2 yaprak) → kapsam toplamı. */
export type KpiTotalKind = "consolidated" | "scope" | "none";
export const KPI_TOTAL_KIND_LABELS: Record<Exclude<KpiTotalKind, "none">, string> = {
  consolidated: "Konsolide",
  scope: "Kapsamınızdaki işletmeler toplamı",
};
/** Kapsamlı çözümün grid'e eklediği tamlık bilgisi (§4.3). */
export interface KpiGridEntityScope {
  /** Kullanıcı tüm aktif yaprakları görüyor mu. */
  full: boolean;
  totalKind: KpiTotalKind;
  /** Grup işletmesi id → kapsamın o grubu TAM kapsadığı yıllar (ara toplam yalnız bunlarda). */
  completeGroups: Record<string, number[]>;
}
export const KpiGridEntityScopeSchema = z.object({
  full: z.boolean(),
  totalKind: z.enum(["consolidated", "scope", "none"]),
  completeGroups: z.record(z.string(), z.array(z.number().int())),
});
export const CLIENT_ENTITY_TYPE_LABELS: Record<ClientEntityType, string> = {
  group: "Grup",
  legal_entity: "Tüzel kişi",
  business_unit: "İş birimi",
  facility: "Tesis",
};
export const KPI_REPORT_TABLE_LAYOUT_LABELS: Record<KpiReportTableLayout, string> = {
  entities: "İşletmeler",
  consolidated: "Konsolide",
  both: "İkisi de",
};
